import { Keys } from '@progress/kendo-react-common';
import React from 'react';
import { Component, Position } from '../../index';
import PuzzleInput from '../UI/fields/input/PuzzleInput';
import {
  DEFAULT_SEARCH_BAR_STATE,
  SEARCH_BAR_PROPS,
  SEARCH_BAR_STATE
} from './Types';
import * as _ from 'lodash';
import { FILTER_END_POINTS } from './filter/EndPoints.const';
import { 
  MaskedTextBoxChangeEvent 
} from '@progress/kendo-react-inputs/dist/npm/maskedtextbox/MaskedTextBoxProps';
import {
  PUZZLE_INPUT_ACTION_CONFIG, 
  PUZZLE_INPUT_KEY_EVENT
} from '../UI/fields/input/Types';

class SearchBar extends Component<SEARCH_BAR_PROPS, SEARCH_BAR_STATE> {
  /**
   * Default component state
   * @private {DEFAULT_SEARCH_BAR_STATE}
   */
  private defaultState: DEFAULT_SEARCH_BAR_STATE = {
    expanded: false,
    value: ''
  }

  /**
   * Component default search field key
   * @private
   */
  private defaultSearchKey: string = 'search';

  public endPoints;

  public getEndPoint = (type: string) => this.endPoints[type];

  public getDashboard = () => this.props.dashboard;

  constructor(props: any) {
    super(props);
    this.endPoints = { ...FILTER_END_POINTS } || {};
    _.merge(this.endPoints, _.get(this, 'props.filterConfig.endPoints') || {});
    this.state = {
      ...this.defaultState
    };
  }

  /**
   * Sets search default state
   */
  resetSearch = () => {
    this.setState(this.defaultState, () => {
      this.onSearch('');
    });
  };

  /**
   * Getter for searchKey value
   */
  getSearchKey = () => this.props.searchKey || this.defaultSearchKey

  /**
   * Sets search value state
   *
   * @param value
   */
  setSearchValue = (value: string = '') => this.setState({ value });

  /**
   * Gets search value state
   */
  getSearchValue = () => this.state.value;

  /**
   * Resets only input field and doesn't reset grid
   */
  resetSearchOnlyInput = () => {
    this.setState(this.defaultState);
  };

  /**
   * Prepares search criteria and triggers filter request
   * @param {string} search
   * @param {any} [filters]
   */
  onSearch = (search: string = '', filters?) => {
    let requestOpts = {
      method: this.props.method || 'get',
      url: this.props.endPoint,
      params: {}
    };

    // adds filter/additional criteria
    if (_.isObject(filters)) {
      requestOpts.params = _.defaults(requestOpts.params, filters);
    }

    search = search.trim ? search.trim() : search;
    const isSearchValid = !_.isEmpty(search);
    const searchSendingValue = isSearchValid ? search : undefined;
    const searchKey = this.getSearchKey();
    _.set(requestOpts.params, searchKey, searchSendingValue);
    requestOpts['saveScroll'] = false;
    requestOpts['filterType'] = 'quickSearch';
    this.cleanupSearchData(requestOpts.params);

    this.dispatch('triggerSearch', {
      pos: Position.end,
      buffer: 200
    }, { ...requestOpts });
  };

  /**
   * Cleanup filter data removes all undefined or empty fields
   * @param data
   */
  cleanupSearchData = (data) => {
    for (let param in data) {
      if (data.hasOwnProperty(param) && _.isEmpty(data[param])) {
        data[param] = null;
      }
    }
  }

  /**
   * Synchronize filter form changes with the SearchBar state
   * in order to preset real time updates in the tag value
   */
  onChange = (event: MaskedTextBoxChangeEvent) => {
    const value = event.target.value as string;
    this.setState({ value });
  };

  /**
   * Triggers onSearch if user clicks Enter key on the keyboard
   * @param {event: KeyboardEvent, target: HTMLElement} e
   */
  onKeyDown = (e: PUZZLE_INPUT_KEY_EVENT):void => {
    const { value } = this.state;
    if (e.event.keyCode === Keys.enter) {
      this.onSearch(value);
    }
  }

  /**
   * Toggles filter dialog
   */
  toggleFilters = () => {
    let expanded = this.state.expanded;
    this.setState({
      expanded: !expanded
    }, () => this.dispatch('OpenFilters', null, {
      ...this.props,
      endPoints: this.endPoints,
      url: this.endPoints.filters,
      urlParams: this.endPoints.urlParams
    }));
  }

  renderBody() {
    let pickKeys = _.keys({
      actionBtnSettings: true,
      inputClassName: true,
      searchKey: true,
      asField: true,
      medium: true,
      errorTxt: true,
      labelClassName: true,
      validationType: true,
      withValidation: true,
      withTooltip: true,
      titleText: true,
      tooltipContent: true,
      tooltipPosition: true,
      tooltipAnchor: true,
      tooltipOpenDelay: true
    });
    const props = _.pick(this.props, pickKeys);
    let placeholder = this.props.placeholder || this.translate('placeholder');
    const { size } = this.props;

    let additionalBtns: PUZZLE_INPUT_ACTION_CONFIG[] = [];
    if (!_.isEmpty(_.get(this, 'props.filterConfig.fields'))) {
      additionalBtns.push({
        buttonType: 'openFilters',
        fillMode: 'flat',
        iconClass: 'tp-icon tp-filter',
        ariaLabel: this.translate('SRL-openFilters'),
        onClick: this.toggleFilters
      });
    }

    additionalBtns.push({
      buttonType: 'delete',
      ariaLabel: this.translate('SRL-clearFilters'),
      onClick: this.resetSearch
    });
    return (
      <div
        className={'tp-searchbar-content'}
      >
        <PuzzleInput
          actionBtnSettings={additionalBtns}
          onChange={this.onChange}
          onKeyDown={this.onKeyDown}
          name="name"
          size={size}
          aria-label={placeholder}
          placeholder={placeholder}
          searchField
          value={this.state.value}
          {...props}
          valid={true}
        />
      </div>
    );
  }
}

export default SearchBar;
